<section class="campaign-map" [class.dashboard-map]="!campaignId()" aria-labelledby="map-title">
  <header class="map-header">
    <div>
      <h2 id="map-title" class="text-base font-bold">Solarpotenzial</h2>
      <p>{{ campaignId() ? 'Gebäude auswählen und Analyse ansehen.' : 'Alle Adressen Ihrer Organisation. Gebäude auswählen und Analysen entdecken.' }}</p>
    </div>
  </header>
  <div class="map-frame">
    <div #mapCanvas class="map-canvas" tabindex="-1" role="region" aria-label="Solarpotenzialkarte. Gebäude lassen sich auch über die Adresssuche auswählen."></div>
    @if (!loading() && !error() && located().length) {
      <div class="map-search">
        <label class="sr-only" for="campaign-map-search">Adresse suchen</label>
        <div class="search-field">
          <ng-icon name="lucideSearch" size="16" aria-hidden="true" />
          <input hlmInput class="map-chrome [&:not(textarea)]:h-8 pl-8 text-xs" id="campaign-map-search" type="search" autocomplete="off" placeholder="Adresse suchen" [value]="query()" (input)="query.set($any($event.target).value)" aria-controls="map-search-results" (keydown.escape)="query.set('')" />
        </div>
        @if (query().trim()) {
          <div id="map-search-results" class="search-results map-chrome">
            @for (row of matches(); track row.analysisId) {
              <button hlmBtn variant="ghost" type="button" (click)="locate(row)">{{ label(row) }}</button>
            } @empty { <p>Keine passende Adresse.</p> }
          </div>
        }
      </div>
      <div class="map-controls map-chrome" [class.controls-bottom]="paint()" role="group" aria-label="Kartensteuerung">
        <button hlmBtn variant="ghost" size="icon" class="size-8" type="button" (click)="zoom(1)" [disabled]="zoomLevel() >= 20" aria-label="Vergrößern" title="Vergrößern"><ng-icon name="lucidePlus" size="16" aria-hidden="true" /></button>
        <button hlmBtn variant="ghost" size="icon" class="size-8" type="button" (click)="zoom(-1)" [disabled]="zoomLevel() <= 0" aria-label="Verkleinern" title="Verkleinern"><ng-icon name="lucideMinus" size="16" aria-hidden="true" /></button>
        <span class="control-divider" aria-hidden="true"></span>
        <button hlmBtn variant="ghost" size="icon" class="size-8" type="button" (click)="fitArea()" aria-label="Gesamtes Gebiet anzeigen" title="Gesamtes Gebiet anzeigen"><ng-icon name="lucideLocateFixed" size="16" aria-hidden="true" /></button>
      </div>
      @if (!paint()) {
      <div class="map-legend map-chrome" aria-label="Farblegende: geplante PV-Leistung in Kilowatt-Peak">
        <span class="legend-title">PV-Leistung <span>kWp</span></span>
        <div class="legend-scale">
          @for (color of colors; track color; let i = $index) {
            <span><i [style.background]="color"></i>{{ ['< 5', '5–10', '10–20', '≥ 20'][i] }}</span>
          }
          <span><i class="unknown"></i>Offen</span>
        </div>
        <div class="map-caption" role="status" aria-live="polite">
          <span>{{ addressCount() | number }} Adressen</span><span aria-hidden="true">·</span>
          @if (mapBusy()) { <span class="loading-dot" aria-hidden="true"></span> }
          @if (resultsLoading() || resultRequests()) { <span>Analysen laden · {{ resultProgress() }}/{{ resultTotal() }}</span> }
          @else if (detailLoading()) { <span>Gebäude laden</span> }
          @else if (tilesLoading()) { <span>Karte laden</span> }
          @else { <span>{{ resultCount() | number }} {{ resultCount() === 1 ? 'Ergebnis' : 'Ergebnisse' }}</span> }
        </div>
      </div>
      }
    }
    @if (loading() || error() || !located().length) {
      <div class="map-state" role="status">
        @if (loading()) { <span class="loading-dot"></span><h3>Karte wird geladen</h3> }
        @else if (error()) { <h3>Karte konnte nicht geladen werden</h3><button hlmBtn variant="outline" (click)="load()">Erneut versuchen</button> }
        @else { <h3>{{ rows().length ? 'Noch keine Kartenpositionen' : 'Noch keine Adressen' }}</h3><p>{{ rows().length ? 'Sobald die Adressen verortet sind, erscheinen sie hier.' : (campaignId() ? 'Die Karte füllt sich mit den Adressen der Kampagne.' : 'Die Karte füllt sich mit den Adressen Ihrer Organisation.') }}</p><button hlmBtn variant="outline" (click)="load()">Aktualisieren</button> }
      </div>
    }
  </div>
  <footer class="map-footer">
    <p>Farbe: Hauptplanung der neuesten abgeschlossenen Analyse je Adresse. Umrisse: OpenStreetMap.</p>
    @if (missing()) { <p>{{ missing() | number }} Adressen ohne Kartenposition.</p> }
    @if (mapError()) { <p role="status">Die Hintergrundkarte ist teilweise nicht verfügbar.</p> }
    @if (footprintError()) { <p role="status">Gebäudeumrisse derzeit nicht verfügbar. Adresspunkte bleiben sichtbar. <button type="button" (click)="retryFootprints()">Erneut versuchen</button></p> }
    @if (resultError()) { <p role="status">Einige Ergebnisse konnten nicht geladen werden. <button type="button" (click)="retryResults()">Erneut versuchen</button></p> }
  </footer>
</section>

<!-- Leaflet mounts this Angular view inside its geographically anchored popup. -->
<div hidden>
  <section #mapPreview class="map-preview" role="dialog" aria-modal="false" aria-labelledby="map-preview-title" tabindex="-1" (keydown.escape)="close(); $event.stopPropagation()">
    @if (selected(); as row) {
      <header class="preview-header">
        <div>
          <span class="preview-eyebrow">Solaranalyse</span>
          <h3 id="map-preview-title">{{ row.address?.street }} {{ row.address?.houseNumber }}@if (!row.address) { {{ row.title || 'Gebäude' }} }</h3>
          <p class="preview-note">{{ row.address?.postalCode }} {{ row.address?.city }}</p>
        </div>
        <button hlmBtn variant="ghost" size="icon" class="size-8 shrink-0" type="button" aria-label="Vorschau schließen" (click)="close()"><ng-icon name="lucideX" size="16" aria-hidden="true" /></button>
      </header>
      @if (previewAnalyses().length > 1) {
        <div class="preview-list-heading"><span>{{ previewAnalyses().length }} Analysen</span><span>Neueste zuerst</span></div>
        <div class="preview-list" role="region" aria-label="Analysen in diesem Gebäude" tabindex="0">
          @for (analysis of previewAnalyses(); track analysis.analysisId) {
            <a class="preview-analysis" [routerLink]="['/organizations', organizationSlug(), 'analyses', analysis.analysisId]" [queryParams]="{ returnTo: returnTo() }">
              <div class="preview-analysis-title"><strong>{{ analysis.address?.street }} {{ analysis.address?.houseNumber }}</strong><ng-icon name="lucideArrowUpRight" size="16" aria-hidden="true" /></div>
              <span class="preview-note">{{ analysis.createdAt | date:'dd.MM.yyyy' }} · {{ analysis.analysisId.slice(-6) }}</span>
              @if (resultFor(analysis)?.plan; as plan) {
                <span class="preview-analysis-result">{{ plan.plantCapacity | number:'1.1-1' }} kWp <span>· {{ plan.numberModels | number }} Module</span></span>
              } @else { <span class="preview-note">{{ previewStatus(analysis) }}</span> }
            </a>
          }
        </div>
      } @else if (row.addressOnly) {
        <p class="preview-note">Für diese Adresse gibt es noch keine Analyse.</p>
      } @else {
      @if (selectionResult()?.plan; as plan) {
        <div class="preview-metrics">
          <div><span>PV-Leistung</span><strong>{{ plan.plantCapacity | number:'1.1-1' }} <small>kWp</small></strong></div>
          <div><span>Solarmodule</span><strong>{{ plan.numberModels | number }}</strong></div>
        </div>
      } @else if (selectionResult()?.error) {
        <p class="preview-note" role="status">Ergebnis konnte nicht geladen werden.</p>
        <button hlmBtn variant="outline" (click)="retryResults()">Erneut versuchen</button>
      } @else if (canLoadPlanning(row) && !selectionResult()) {
        <p class="preview-note status-loading" role="status"><span class="loading-dot" aria-hidden="true"></span>Analyse wird geladen …</p>
      } @else {
        <p class="preview-note">Noch keine abgeschlossene Hauptplanung.</p>
      }
      @if (!selectionHasFootprint()) { <p class="preview-note">Adressposition · Gebäudeumriss nicht zugeordnet.</p> }
      <a hlmBtn class="w-full" [routerLink]="['/organizations', organizationSlug(), 'analyses', row.analysisId]" [queryParams]="{ returnTo: returnTo() }">Analyse öffnen <ng-icon name="lucideArrowUpRight" size="16" aria-hidden="true" /></a>
      }
    }
  </section>
</div>
